<template>
  <b-container fluid>
    <backlink />
    <h1>Frais à facturer</h1>

    <p class="pending-fee__intro">
      Montants dus hors du cycle de location : frais de location pour compte, régularisations de
      transport et soldes d'engagement longue durée. Tant qu'une ligne est <em>à facturer</em>,
      elle reste corrigeable — c'est la dernière occasion de la reprendre avant qu'elle ne parte
      chez le client.
    </p>

    <!-- Les chiffres avant la liste : cet écran sert d'abord à anticiper le prochain cycle,
         et la question posée est « combien part, sur quoi » — pas « quelles lignes exactement »,
         qui vient après. Le gabarit `divided` est celui des indicateurs de la fiche référence. -->
    <div class="ibox">
      <div class="ibox-content divided">
        <b-row>
          <b-col cols="auto" class="pending-fee__figure pending-fee__figure--total flex-fill">
            <dl>
              <dd>Total HT</dd>
              <dt>{{ stateLabel }}</dt>
            </dl>
            <h2>{{ formatAmount(total) }}</h2>
          </b-col>
          <b-col cols="auto" class="pending-fee__figure flex-fill">
            <dl>
              <dd>Lignes</dd>
              <dt>correspondant au filtre</dt>
            </dl>
            <h2>{{ count }}</h2>
          </b-col>
          <b-col cols="auto" class="pending-fee__figure flex-fill">
            <dl>
              <dd>Origines</dd>
              <dt>représentées</dt>
            </dl>
            <h2>{{ originCount }}</h2>
          </b-col>
          <b-col cols="auto" class="pending-fee__figure flex-fill">
            <dl>
              <dd>Ligne la plus ancienne</dd>
              <dt>date de rattachement</dt>
            </dl>
            <h2>{{ oldestAttachedAt ? $moment(oldestAttachedAt).format("DD/MM/YYYY") : "—" }}</h2>
          </b-col>
        </b-row>
      </div>
    </div>

    <!-- Statuts en onglets au-dessus des filtres, comme les listes du back-office. Les
         compteurs suivent les AUTRES filtres : un onglet qui annoncerait le total général
         pendant qu'une agence est sélectionnée induirait en erreur. -->
    <div class="tabs d-flex flex-row">
      <div :class="{ tab: true, active: state === null }" @click="setState(null)">
        Tout <span class="tab__count">{{ (countsByState.to_bill || 0) + (countsByState.billed || 0) }}</span>
      </div>
      <div :class="{ tab: true, active: state === 'to_bill' }" @click="setState('to_bill')">
        À facturer <span class="tab__count">{{ countsByState.to_bill || 0 }}</span>
      </div>
      <div :class="{ tab: true, active: state === 'billed' }" @click="setState('billed')">
        Facturées <span class="tab__count">{{ countsByState.billed || 0 }}</span>
      </div>
    </div>

    <div class="ibox">
      <div class="ibox-content">
        <div class="filters">
          <div class="d-flex align-items-center">
            <div class="flex-grow-1 search-bar">
              <b-input-group>
                <label class="mb-0 align-self-center"><i class="far fa-search fa-lg"></i></label>
                <b-form-input v-model="search" placeholder="Libellé, n° de demande, de commande ou d'affaire"
                  @change="load" />
              </b-input-group>
            </div>
            <div class="ml-2">
              <b-button variant="outline-primary" size="sm" @click="exportCsv" :disabled="!rows.length">
                <i class="far fa-file-export"></i> Exporter en CSV
              </b-button>
            </div>
            <div class="ml-2 filters-toggle" @click.prevent="resetFilters()">
              <a href>Reset <i class="fa fa-undo ml-1"></i></a>
            </div>
          </div>

          <b-form class="extended-filters">
            <b-row>
              <b-col class="filter-col">
                <b-form-group label="Origine :" label-for="filter-origin">
                  <b-form-select id="filter-origin" v-model="origin" :options="originOptions" @change="load">
                    <template slot="first"><option :value="null">-- Toutes --</option></template>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col class="filter-col">
                <b-form-group label="Catégorie :" label-for="filter-category">
                  <b-form-select id="filter-category" v-model="category" :options="categoryOptions" @change="load">
                    <template slot="first"><option :value="null">-- Toutes --</option></template>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col class="filter-col">
                <b-form-group label="Agence :" label-for="filter-agency">
                  <AgencyAutocomplete id="filter-agency" v-model="agency" placeholder="Toutes les agences"
                    @input="load" />
                </b-form-group>
              </b-col>
              <b-col class="filter-col">
                <b-form-group label="Affaire :" label-for="filter-business">
                  <b-form-input id="filter-business" v-model="business" placeholder="N° d'affaire" @change="load" />
                </b-form-group>
              </b-col>
              <b-col class="filter-col">
                <b-form-group label="Rattachée du :" label-for="filter-from">
                  <b-form-input id="filter-from" type="date" v-model="attachedFrom" @change="load" />
                </b-form-group>
              </b-col>
              <b-col class="filter-col">
                <b-form-group label="Au :" label-for="filter-to">
                  <b-form-input id="filter-to" type="date" v-model="attachedTo" @change="load" />
                </b-form-group>
              </b-col>
              <b-col class="filter-col">
                <b-form-group label="N° de facture :" label-for="filter-billing">
                  <b-form-input id="filter-billing" v-model="billingNumber" placeholder="N° de facture"
                    @change="load" />
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </div>

        <div v-if="isBusy" class="loader"></div>

        <div v-else-if="rows.length" class="table-wrapper">
          <b-table small hover :items="rows" :fields="fields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
            :per-page="perPage" :current-page="currentPage">
            <template slot="origin_label" slot-scope="data">
              <span class="pending-fee__origin">{{ data.value }}</span>
            </template>
            <!-- La référence d'origine ouvre sa pièce : une demande, une commande ou une
                 référence unique selon l'origine — l'écran ne devine pas la route, l'API donne
                 l'identifiant qui va avec. -->
            <template slot="origin_reference" slot-scope="data">
              <router-link v-if="data.item.location_for_account_id"
                :to="{ name: 'LocationForAccountDetail', params: { id: data.item.location_for_account_id } }">
                {{ data.value }}
              </router-link>
              <router-link v-else-if="data.item.renting_id"
                :to="{ name: 'RentingDetail', params: { id: data.item.renting_id } }">
                {{ data.value }}
              </router-link>
              <router-link v-else-if="data.item.reference_id"
                :to="{ name: 'ReferenceDetail', params: { id: data.item.reference_id } }">
                {{ data.value }}
              </router-link>
              <template v-else>{{ data.value || "—" }}</template>
            </template>
            <template slot="label" slot-scope="data">
              {{ data.value }}
              <span class="pending-fee__detail">{{ data.item.quantity }} × {{ formatAmount(data.item.unit_price_ht) }}</span>
            </template>
            <template slot="category_label" slot-scope="data">
              <b-badge variant="light" class="pending-fee__category">{{ data.value }}</b-badge>
            </template>
            <template slot="attached_at" slot-scope="data">{{ data.value | moment("DD/MM/YYYY") }}</template>
            <template slot="total_ht" slot-scope="data"><strong>{{ formatAmount(data.value) }}</strong></template>
            <template slot="state_label" slot-scope="data">
              <span :class="data.item.locked ? 'pending-fee__state pending-fee__state--billed' : 'pending-fee__state'">
                {{ data.value }}
              </span>
              <!-- Une ligne facturée garde un chemin vers sa pièce comptable : c'est le seul
                   accès que le SFD lui laisse, avec la lecture. -->
              <router-link v-if="data.item.manual_billing_id" class="pending-fee__detail"
                :to="{ name: 'ManualBillingDetail', params: { id: data.item.manual_billing_id } }">
                {{ data.item.manual_billing_number }}
              </router-link>
            </template>
            <!-- Une ligne facturée est une pièce comptable : elle n'offre plus que la lecture. -->
            <template slot="actions" slot-scope="data">
              <b-button-group size="sm" v-if="!data.item.locked">
                <b-button variant="outline-secondary" v-b-tooltip.hover title="Modifier" @click="openEdit(data.item)">
                  <i class="far fa-pencil"></i>
                </b-button>
                <b-button variant="outline-secondary" v-b-tooltip.hover title="Supprimer"
                  @click="askDelete(data.item)">
                  <i class="far fa-trash-alt"></i>
                </b-button>
              </b-button-group>
              <span v-else class="text-muted" v-b-tooltip.hover title="Ligne facturée, verrouillée">
                <i class="far fa-lock"></i>
              </span>
            </template>
          </b-table>

          <div class="pending-fee__footer">
            <b-pagination v-if="count > perPage" v-model="currentPage" :total-rows="count" :per-page="perPage"
              class="mb-0" />
            <span v-else>{{ count }} ligne(s)</span>
            <strong>{{ formatAmount(total) }}</strong>
          </div>
        </div>

        <p v-else class="pending-fee__empty">
          <i class="far fa-check-circle"></i>
          Aucune ligne ne correspond à ce filtre.
        </p>
      </div>
    </div>

    <!-- `@ok.prevent` : la fenêtre ne se ferme qu'une fois le serveur d'accord, sans quoi un
         champ oublié ferait disparaître la saisie avec elle. -->
    <b-modal id="pending-fee-edit" title="Modifier la ligne" ok-title="Enregistrer" cancel-title="Annuler"
      @ok.prevent="submitEdit" @hidden="editErrors = {}">
      <b-form-group label="Libellé *">
        <b-form-input v-model="editForm.label" />
        <div v-if="editErrors.label" class="text-danger pending-fee__error">{{ editErrors.label }}</div>
      </b-form-group>
      <b-form-group label="Catégorie">
        <b-form-select v-model="editForm.category" :options="categoryOptions" />
      </b-form-group>
      <b-form-group label="Quantité *">
        <b-form-input type="number" step="0.01" v-model="editForm.quantity" />
      </b-form-group>
      <b-form-group label="Montant unitaire HT *">
        <b-form-input type="number" step="0.01" v-model="editForm.unit_price_ht" />
      </b-form-group>
      <b-form-group label="Date de rattachement *"
        description="C'est elle qui détermine le cycle de facturation qui portera la ligne.">
        <b-form-input type="date" v-model="editForm.attached_at" />
      </b-form-group>
      <b-form-group label="Commentaire">
        <b-form-textarea v-model="editForm.comment" rows="2" />
      </b-form-group>
    </b-modal>

    <b-modal id="pending-fee-delete" title="Supprimer la ligne" ok-title="Supprimer" ok-variant="danger"
      cancel-title="Annuler" @ok="confirmDelete">
      <p v-if="toDelete">
        Supprimer <strong>{{ toDelete.label }}</strong> ({{ formatAmount(toDelete.total_ht) }}) ?
        Elle ne sera pas reprise au prochain cycle de facturation.
      </p>
    </b-modal>
  </b-container>
</template>

<script>
export default {
  name: "PendingFeeList",
  data() {
    return {
      rows: [],
      total: 0,
      count: 0,
      countsByState: {},
      isBusy: false,
      // « À facturer » est l'onglet qu'on vient chercher : c'est ce qui partira au prochain
      // cycle, et donc ce qu'il reste à corriger.
      state: "to_bill",
      search: null,
      origin: null,
      category: null,
      agency: null,
      business: null,
      attachedFrom: null,
      attachedTo: null,
      billingNumber: null,
      originOptions: [],
      categoryOptions: [],
      sortBy: "attached_at",
      sortDesc: true,
      perPage: 25,
      currentPage: 1,
      editForm: {},
      editErrors: {},
      editing: null,
      toDelete: null,
      fields: [
        { key: "origin_label", label: "Origine", sortable: true },
        { key: "origin_reference", label: "Référence", sortable: true },
        { key: "agency", label: "Agence", sortable: true },
        { key: "business", label: "Affaire", sortable: true },
        { key: "label", label: "Libellé", sortable: true },
        { key: "category_label", label: "Catégorie", sortable: true },
        { key: "attached_at", label: "Rattachement", sortable: true },
        { key: "total_ht", label: "Total HT", sortable: true, class: "text-right" },
        { key: "state_label", label: "État", sortable: true },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },
  computed: {
    stateLabel() {
      if (this.state === "to_bill") return "en attente du prochain cycle";
      if (this.state === "billed") return "déjà facturé";

      return "toutes lignes confondues";
    },
    originCount() {
      return new Set(this.rows.map((row) => row.origin)).size;
    },
    /**
     * Une ligne qui traîne depuis deux cycles signale une pièce oubliée — c'est le seul
     * endroit où cela se voit.
     */
    oldestAttachedAt() {
      return this.rows.reduce(
        (oldest, row) => (!oldest || row.attached_at < oldest ? row.attached_at : oldest),
        null
      );
    },
  },
  created() {
    this.loadOptions();
    this.load();
  },
  methods: {
    /** Origines, états et catégories viennent de l'API : ce sont les libellés des factures. */
    loadOptions() {
      this.$http
        .get(process.env.BACK_API_URL + "billable-fee/options")
        .then((response) => {
          const toOptions = (dict) =>
            Object.keys(dict || {}).map((value) => ({ value, text: dict[value] }));
          this.originOptions = toOptions(response.data.origins);
          this.categoryOptions = toOptions(response.data.categories);
        })
        .catch(() => {
          this.$toastr.error("Le chargement des origines a échoué");
        });
    },
    load() {
      this.isBusy = true;
      this.currentPage = 1;
      this.$http
        .get(process.env.BACK_API_URL + "billable-fee", {
          params: {
            search: this.search,
            origin: this.origin,
            state: this.state,
            category: this.category,
            agency: this.agency,
            business: this.business,
            attachedFrom: this.attachedFrom,
            attachedTo: this.attachedTo,
            billingNumber: this.billingNumber,
          },
        })
        .then((response) => {
          this.rows = response.data.billableFees || [];
          this.total = response.data.total || 0;
          this.count = response.data.count || 0;
          this.countsByState = response.data.countsByState || {};
        })
        .catch(() => {
          this.$toastr.error("Le chargement des frais à facturer a échoué");
          this.rows = [];
          this.total = 0;
          this.count = 0;
        })
        .then(() => {
          this.isBusy = false;
        });
    },
    setState(state) {
      this.state = state;
      this.load();
    },
    resetFilters() {
      this.search = null;
      this.origin = null;
      this.category = null;
      this.agency = null;
      this.business = null;
      this.attachedFrom = null;
      this.attachedTo = null;
      this.billingNumber = null;
      this.load();
    },
    openEdit(row) {
      this.editing = row;
      this.editErrors = {};
      this.editForm = {
        label: row.label,
        category: row.category,
        quantity: row.quantity,
        unit_price_ht: row.unit_price_ht,
        attached_at: row.attached_at,
        comment: row.comment,
      };
      this.$root.$emit("bv::show::modal", "pending-fee-edit");
    },
    submitEdit() {
      const errors = {};
      if (!this.editForm.label) {
        errors.label = "Ce champ est obligatoire.";
      }

      this.editErrors = errors;
      if (Object.keys(errors).length) {
        return;
      }

      this.$http
        .post(process.env.BACK_API_URL + "billable-fee/" + this.editing.id, this.editForm)
        .then(() => {
          this.$root.$emit("bv::hide::modal", "pending-fee-edit");
          this.load();
        })
        .catch((error) => {
          // Le serveur refuse aussi une ligne déjà facturée : son message est plus précis que
          // tout ce qu'on pourrait dire ici.
          const errs = error.response && error.response.data && error.response.data.errors;
          this.$toastr.error(errs && errs.length ? errs[0] : "La modification a échoué");
        });
    },
    askDelete(row) {
      this.toDelete = row;
      this.$root.$emit("bv::show::modal", "pending-fee-delete");
    },
    confirmDelete() {
      this.$http
        .delete(process.env.BACK_API_URL + "billable-fee/" + this.toDelete.id)
        .then(() => this.load())
        .catch(() => this.$toastr.error("La suppression a échoué"));
    },
    formatAmount(value) {
      return parseFloat(value || 0).toFixed(2).replace(".", ",") + " €";
    },
    exportCsv() {
      const header = [
        "Origine", "Référence", "Agence", "Affaire", "Libellé", "Catégorie",
        "Quantité", "PU HT", "Total HT", "Rattachement", "État", "N° de facture",
      ];
      const lines = this.rows.map((row) =>
        [
          row.origin_label, row.origin_reference, row.agency, row.business, row.label,
          row.category_label, row.quantity,
          String(row.unit_price_ht).replace(".", ","),
          String(row.total_ht).replace(".", ","),
          this.$moment(row.attached_at).format("DD/MM/YYYY"),
          row.state_label, row.manual_billing_number,
        ]
          // Le séparateur est le point-virgule, comme les autres exports du back-office :
          // c'est ce qu'attend un Excel configuré en français.
          .map((v) => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"')
          .join(";")
      );

      const blob = new Blob(["﻿" + [header.join(";")].concat(lines).join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "frais-a-facturer-" + new Date().toISOString().slice(0, 10) + ".csv";
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

.pending-fee__intro {
  max-width: 95ch;
  margin-bottom: 20px;
  color: $text-50p;
}

// Indicateur : le libellé en petit, la valeur en grand. Même forme que les indicateurs de la
// fiche référence, dont cet écran est le voisin fonctionnel.
.pending-fee__figure {
  display: flex;
  align-items: center;
  justify-content: space-between;

  dl { margin: 0; }
  dd { margin: 0; font-weight: 700; }
  dt { font-weight: 400; color: $text-50p; font-size: 11px; }
  h2 { margin: 0 0 0 15px; font-size: 24px; white-space: nowrap; }

  // Le total est ce qu'on vient chercher : il est le seul en couleur.
  &--total h2 { color: $primary; }
}

// Onglets et filtres repris d'`AdminTableSync`, dont le SCSS est `scoped` et ne peut donc pas
// être hérité. La duplication est assumée : sortir ces styles en global toucherait toutes les
// listes du back-office, ce qui n'est pas le sujet de cet écran.
.tabs {
  background-color: $bg-1;
  width: 100%;
  overflow: hidden;

  .tab {
    padding: 15px 20px;
    text-align: center;
    text-transform: uppercase;
    @extend .clanprobold;
    color: $primary-darker;
    font-size: 13px;
    opacity: 0.5;
    cursor: pointer;

    &.active {
      color: $primary;
      opacity: 1;
      background-color: #fff;
      box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    }
  }

  .tab__count {
    display: inline-block;
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.06);
    font-size: 11px;
  }
}

.filters {
  border: solid 1px $border-10p;
  padding: 6px 20px;
  margin-bottom: 20px;

  .search-bar {
    color: $text-50p;

    input { border: none; @extend .clanprobook; }
  }

  .filters-toggle a {
    @extend .clanprobold;
    color: $text-50p;
    text-transform: uppercase;
  }

  // Filtres étendus : séparés de la barre d'actions par un filet à fond perdu (marges
  // négatives compensant le padding de `.filters`).
  .extended-filters {
    border-top: solid 1px $border-10p;
    margin: 6px -20px 0;
    padding: 16px 20px 0;
  }

  .filter-col { flex-basis: 25%; }

  @media (max-width: 768px) {
    .filter-col { flex: 0 0 100%; max-width: 100%; }
  }
}

// L'origine se lit d'un coup d'œil : c'est la première colonne, et ce qui distingue les
// lignes entre elles. La distinction tient à la forme — petites capitales — et non à une
// couleur par origine : le tableau en portait trois, sans qu'aucune ne veuille dire quelque
// chose de plus que le mot qu'elle colorait.
.pending-fee__origin {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03rem;
}

// Le calcul sous le libellé, le n° de facture sous l'état : ce qui justifie une valeur se lit
// après elle, en plus discret.
.pending-fee__detail {
  display: block;
  font-size: 11px;
  color: $text-50p;
}

.pending-fee__category {
  font-weight: 400;
  background-color: $bg-1;
  color: $text-50p;
}

.pending-fee__state--billed { color: $text-50p; }

.pending-fee__footer {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 15px 25px 0;
  border-top: solid 1px $border-10p;
  color: $text-50p;

  strong { font-size: 18px; color: $primary; }
}

.pending-fee__empty {
  margin: 0;
  color: $text-50p;

  i { margin-right: 8px; color: $primary; }
}

.pending-fee__error {
  margin-top: 6px;
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/PendingFee/PendingFeeList.vue